import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule } from '@angular/common';

//angular functions
import { RouterOutlet } from '@angular/router';
import { RouterLink } from '@angular/router';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

//bootstrap
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbPaginationModule, NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';

//angular material
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';

//components
import { NavbarComponent } from "./navbar/navbar.component";
import { FooterComponent } from './footer/footer.component';
import { DingoAiWidgetComponent } from './dingo-ai-widget/dingo-ai-widget.component';
import { environment } from '../environments/environment';

//icons
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { faLaptopCode } from '@fortawesome/free-solid-svg-icons';
import {
  LEGACY_SERVICE_DETAIL_SELECTOR,
  initLegacyServiceDetailReveal,
  refreshGlobalScrollTriggers
} from './shared/scroll-reveal.util';


@Component({
    selector: 'app-root',
    standalone: true,
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    imports: [CommonModule, RouterOutlet, RouterLink, NavbarComponent, FooterComponent, DingoAiWidgetComponent, NgbModule, MatMenuModule, MatButtonModule, FontAwesomeModule, NgbPaginationModule, NgbAlertModule, NgbAlert],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'grupodingo';
  showStartupPopup = false;
  isStartupPopupClosing = false;
  readonly calendlyUrl = (environment.calendlyUrl || '').trim();
  readonly hasCalendlyUrl = !!this.calendlyUrl && !this.calendlyUrl.includes('TU_USUARIO');
  readonly startupPopupImageUrl = 'assets/brand/pop-up/pop-up.jpg';
  // Dejar en false mientras se hacen pruebas. En true, recuerda cierre por sesion.
  readonly popupCooldownEnabled = false;
  private readonly popupStorageKey = 'grupodingo_startup_popup_closed';
  private readonly startupPopupDelayMs = 1600;
  private readonly startupPopupAfterHeroDelayMs = 200;
  private readonly startupPopupHeroFallbackMs = 8000;
  private startupPopupTimer: ReturnType<typeof setTimeout> | null = null;
  private startupPopupCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private heroIntroReadyListener: EventListener | null = null;
  private routeEventsSub?: Subscription;
  private routeRevealCleanups: Array<() => void> = [];
  //fontawesome icons
  faLaptopCode = faLaptopCode;
  //fontawesome icons

  constructor(private router: Router, @Inject(PLATFORM_ID) private platformId: Object) {}
  
  //function to charge component always on top + report page view to Google Ads on route change
  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
      const wasClosed = this.popupCooldownEnabled && sessionStorage.getItem(this.popupStorageKey) === '1';
      if (!wasClosed) {
        this.scheduleStartupPopup();
      }
      this.setupRouteRevealAnimations();
      this.routeEventsSub = this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ).subscribe((event) => {
        this.resetRouteScroll();
        this.refreshScrollSystems();
        this.setupRouteRevealAnimations();
        // Notificar a Google Ads/Analytics cada cambio de sección (SPA)
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'page_view', {
            page_path: event.urlAfterRedirects,
            page_title: document.title
          });
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.routeEventsSub?.unsubscribe();
    this.clearStartupPopupTimer();
    this.clearStartupPopupCloseTimer();
    this.removeHeroIntroListener();
    this.clearRouteRevealAnimations();
  }

  closeStartupPopup(): void {
    if (!this.showStartupPopup || this.isStartupPopupClosing) return;
    this.isStartupPopupClosing = true;
    this.clearStartupPopupTimer();
    this.removeHeroIntroListener();
    if (this.popupCooldownEnabled && isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem(this.popupStorageKey, '1');
    }
    this.startupPopupCloseTimer = setTimeout(() => {
      this.showStartupPopup = false;
      this.isStartupPopupClosing = false;
      this.startupPopupCloseTimer = null;
    }, 360);
  }

  openCalendly(): void {
    if (!this.hasCalendlyUrl) return;
    window.open(this.calendlyUrl, '_blank', 'noopener,noreferrer');
  }

  private scheduleStartupPopup(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const path = this.router.url.split('?')[0];
    const isHomeRoute = path === '/' || path === '/inicio';

    if (!isHomeRoute) {
      this.startupPopupTimer = setTimeout(() => {
        this.isStartupPopupClosing = false;
        this.showStartupPopup = true;
      }, this.startupPopupDelayMs);
      return;
    }

    const onHeroReady: EventListener = () => {
      this.removeHeroIntroListener();
      this.clearStartupPopupTimer();
      this.startupPopupTimer = setTimeout(() => {
        this.isStartupPopupClosing = false;
        this.showStartupPopup = true;
      }, this.startupPopupAfterHeroDelayMs);
    };

    this.heroIntroReadyListener = onHeroReady;
    window.addEventListener('dingo:hero-intro-complete', onHeroReady, { once: true });

    this.startupPopupTimer = setTimeout(() => {
      this.removeHeroIntroListener();
      this.isStartupPopupClosing = false;
      this.showStartupPopup = true;
    }, this.startupPopupHeroFallbackMs);
  }

  private clearStartupPopupTimer(): void {
    if (this.startupPopupTimer !== null) {
      clearTimeout(this.startupPopupTimer);
      this.startupPopupTimer = null;
    }
  }

  private clearStartupPopupCloseTimer(): void {
    if (this.startupPopupCloseTimer !== null) {
      clearTimeout(this.startupPopupCloseTimer);
      this.startupPopupCloseTimer = null;
    }
  }

  private removeHeroIntroListener(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.heroIntroReadyListener) return;
    window.removeEventListener('dingo:hero-intro-complete', this.heroIntroReadyListener);
    this.heroIntroReadyListener = null;
  }

  private resetRouteScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const reset = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      document.getElementById('content')?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
    };

    reset();
    requestAnimationFrame(() => {
      reset();
      requestAnimationFrame(reset);
    });
    window.setTimeout(reset, 80);
    window.setTimeout(reset, 260);
  }

  private refreshScrollSystems(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const refresh = () => {
      void refreshGlobalScrollTriggers();
    };

    requestAnimationFrame(refresh);
    window.setTimeout(refresh, 300);
    window.setTimeout(refresh, 900);
  }

  private clearRouteRevealAnimations(): void {
    this.routeRevealCleanups.forEach((cleanup) => cleanup());
    this.routeRevealCleanups = [];
  }

  private setupRouteRevealAnimations(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.clearRouteRevealAnimations();

    let setupStarted = false;
    const setup = async () => {
      if (setupStarted) return;

      const serviceRoot = document.querySelector<HTMLElement>(LEGACY_SERVICE_DETAIL_SELECTOR);
      if (!serviceRoot) return;

      setupStarted = true;
      await initLegacyServiceDetailReveal(serviceRoot, this.routeRevealCleanups);
    };

    requestAnimationFrame(() => {
      void setup();
    });
    window.setTimeout(() => {
      void setup();
    }, 260);
  }
}
